import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef, useState } from "react";
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import { Button, ConfirmModal } from "@libs/rubick";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { useApi } from "../../../app/apiContext";
export function JoinClusterModal({ isOpen, onClose }) {
    const [controlPlaneUrl, setControlPlaneUrl] = useState("");
    const [joinToken, setJoinToken] = useState("");
    const [description, setDescription] = useState("");
    const [modalStep, setModalStep] = useState({
        step: "form"
    });
    const modalStepRef = useRef(modalStep);
    modalStepRef.current = modalStep;
    const navigate = useNavigate();
    const { clustersApi, statusApi } = useApi();
    const [joinCluster, { isLoading }] = clustersApi.useJoinClusterMutation();
    const { refetch } = statusApi.useGetNodeConfigQuery();
    const handleSubmitForm = ()=>{
        setModalStep({
            step: "confirm"
        });
    };
    const handleConfirmJoin = async ()=>{
        try {
            const result = await joinCluster({
                control_plane_url: controlPlaneUrl,
                join_token: joinToken,
                description: description || null
            }).unwrap();
            setModalStep({
                step: "result",
                status: "success",
                message: result.message || "Successfully joined the cluster!"
            });
        } catch (error) {
            setModalStep({
                step: "result",
                status: "error",
                message: error?.data?.message || "Failed to join cluster: Invalid token or control plane URL unreachable"
            });
        }
    };
    const handleClose = ()=>{
        onClose();
    };
    const handleAfterLeave = ()=>{
        const wasSuccessful = modalStepRef.current.step === "result" && modalStepRef.current.status === "success";
        setControlPlaneUrl("");
        setJoinToken("");
        setDescription("");
        setModalStep({
            step: "form"
        });
        if (wasSuccessful) {
            refetch();
            navigate("/", {
                replace: true
            });
        }
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Transition, {
                appear: true,
                show: isOpen && modalStep.step === "form",
                children: /*#__PURE__*/ _jsxs(Dialog, {
                    className: "relative z-50",
                    onClose: handleClose,
                    children: [
                        /*#__PURE__*/ _jsx(TransitionChild, {
                            enter: "ease-out duration-300",
                            enterFrom: "opacity-0",
                            enterTo: "opacity-100",
                            leave: "ease-in duration-200",
                            leaveFrom: "opacity-100",
                            leaveTo: "opacity-0",
                            children: /*#__PURE__*/ _jsx("div", {
                                className: "fixed inset-0 bg-black bg-opacity-50"
                            })
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "fixed inset-0 overflow-y-auto",
                            children: /*#__PURE__*/ _jsx("div", {
                                className: "flex min-h-full items-center justify-center p-4 text-center",
                                children: /*#__PURE__*/ _jsx(TransitionChild, {
                                    enter: "ease-out duration-300",
                                    enterFrom: "opacity-0 scale-95",
                                    enterTo: "opacity-100 scale-100",
                                    leave: "ease-in duration-200",
                                    leaveFrom: "opacity-100 scale-100",
                                    leaveTo: "opacity-0 scale-95",
                                    children: /*#__PURE__*/ _jsxs(DialogPanel, {
                                        className: "w-full max-w-md transform overflow-visible rounded-xl bg-white p-6 text-left align-middle shadow-xl transition-all",
                                        children: [
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center justify-between mb-6",
                                                children: [
                                                    /*#__PURE__*/ _jsx(DialogTitle, {
                                                        as: "h3",
                                                        className: "text-xl font-semibold text-gray-900",
                                                        children: "Join Existing Cluster"
                                                    }),
                                                    /*#__PURE__*/ _jsx("button", {
                                                        onClick: handleClose,
                                                        className: "p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100",
                                                        children: /*#__PURE__*/ _jsx(XMarkIcon, {
                                                            className: "h-5 w-5"
                                                        })
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "space-y-4",
                                                children: [
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        children: [
                                                            /*#__PURE__*/ _jsx("label", {
                                                                className: "block text-sm font-medium text-gray-700 mb-1",
                                                                children: "Control Plane URL"
                                                            }),
                                                            /*#__PURE__*/ _jsx("input", {
                                                                type: "text",
                                                                value: controlPlaneUrl,
                                                                onChange: (e)=>setControlPlaneUrl(e.target.value),
                                                                className: "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors border-gray-300",
                                                                placeholder: "https://control-plane.example.com",
                                                                "data-testid": "control-plane-url-input"
                                                            })
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        children: [
                                                            /*#__PURE__*/ _jsx("label", {
                                                                className: "block text-sm font-medium text-gray-700 mb-1",
                                                                children: "Join Token"
                                                            }),
                                                            /*#__PURE__*/ _jsx("input", {
                                                                type: "text",
                                                                value: joinToken,
                                                                onChange: (e)=>setJoinToken(e.target.value),
                                                                className: "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors border-gray-300",
                                                                placeholder: "Enter the join token",
                                                                "data-testid": "join-token-input"
                                                            })
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        children: [
                                                            /*#__PURE__*/ _jsx("label", {
                                                                className: "block text-sm font-medium text-gray-700 mb-1",
                                                                children: "Node Description (Optional)"
                                                            }),
                                                            /*#__PURE__*/ _jsx("input", {
                                                                type: "text",
                                                                value: description,
                                                                onChange: (e)=>setDescription(e.target.value),
                                                                className: "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors border-gray-300",
                                                                placeholder: "Cluster Node",
                                                                "data-testid": "description-input"
                                                            })
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        className: "flex justify-end space-x-2 pt-4",
                                                        children: [
                                                            /*#__PURE__*/ _jsx(Button, {
                                                                type: "button",
                                                                variant: "simple",
                                                                color: "red",
                                                                onClick: handleClose,
                                                                children: "Cancel"
                                                            }),
                                                            /*#__PURE__*/ _jsx(Button, {
                                                                type: "button",
                                                                variant: "simple",
                                                                color: "navy-90",
                                                                onClick: handleSubmitForm,
                                                                disabled: !controlPlaneUrl || !joinToken || isLoading,
                                                                children: "Continue"
                                                            })
                                                        ]
                                                    })
                                                ]
                                            })
                                        ]
                                    })
                                })
                            })
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(ConfirmModal, {
                isOpen: isOpen && modalStep.step === "confirm",
                onClose: ()=>setModalStep({
                        step: "form"
                    }),
                onConfirm: handleConfirmJoin,
                title: "Warning",
                message: "This action is irreversible. All data (users, API keys) saved on this node will be erased.",
                confirmText: "Join Cluster",
                cancelText: "Cancel",
                variant: "danger",
                closeOnConfirm: false,
                isLoading: isLoading
            }),
            /*#__PURE__*/ _jsx(ConfirmModal, {
                isOpen: isOpen && modalStep.step === "result",
                onClose: handleClose,
                onConfirm: handleClose,
                title: "Join Cluster",
                message: "",
                success: modalStep.step === "result" && modalStep.status === "success",
                successMessage: modalStep.step === "result" ? modalStep.message : "",
                error: modalStep.step === "result" && modalStep.status === "error" ? modalStep.message : null,
                afterLeave: handleAfterLeave
            })
        ]
    });
}
